/**
 * Config Store
 *
 * Zustand store for managing application configuration loaded from the server.
 * Includes subscription prices and PayPal configuration.
 *
 * @module store/configStore
 */

import { create } from 'zustand';
import api from '../services/api';

/**
 * Public configuration from server
 */
export interface PublicConfig {
  freeAccount: {
    operationLimit: number;
    accountLimit: number;
  };
  subscription: {
    monthlyPrice: number;
    annualPrice: number;
    annualMonthlyEquivalent: number;
    annualSavingsPercent: number;
  };
  paypal: {
    clientId: string;
    planId: string;
  };
}

/**
 * Config store state
 */
interface ConfigState {
  /** Whether the config has been loaded */
  isLoaded: boolean;
  /** Whether the config is currently loading */
  isLoading: boolean;
  /** Error that occurred during loading */
  error: string | null;
  /** Free account limits */
  freeOperationLimit: number;
  freeAccountLimit: number;
  /** Subscription pricing */
  monthlyPrice: number;
  annualPrice: number;
  annualMonthlyEquivalent: number;
  annualSavingsPercent: number;
  /** PayPal configuration */
  paypalClientId: string;
  paypalPlanId: string;
  /** Load configuration from server */
  loadConfig: () => Promise<void>;
  /** Reset store to initial state */
  reset: () => void;
}

/**
 * Initial state values
 */
const initialState = {
  isLoaded: false,
  isLoading: false,
  error: null,
  freeOperationLimit: 50,
  freeAccountLimit: 1,
  monthlyPrice: 0.49,
  annualPrice: 4.20,
  annualMonthlyEquivalent: 0.35,
  annualSavingsPercent: 29,
  paypalClientId: '',
  paypalPlanId: '',
};

/**
 * Config store instance
 */
export const useConfigStore = create<ConfigState>((set, get) => ({
  ...initialState,

  loadConfig: async () => {
    // Don't reload if already loading
    if (get().isLoading) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const response = await api.get<{ success: boolean; data: PublicConfig }>(
        '/api/public/config'
      );

      if (response.data.success && response.data.data) {
        const config = response.data.data;
        set({
          isLoaded: true,
          isLoading: false,
          error: null,
          freeOperationLimit: config.freeAccount.operationLimit,
          freeAccountLimit: config.freeAccount.accountLimit,
          monthlyPrice: config.subscription.monthlyPrice,
          annualPrice: config.subscription.annualPrice,
          annualMonthlyEquivalent: config.subscription.annualMonthlyEquivalent,
          annualSavingsPercent: config.subscription.annualSavingsPercent,
          paypalClientId: config.paypal.clientId,
          paypalPlanId: config.paypal.planId,
        });
      } else {
        set({
          isLoading: false,
          error: 'Configuration invalide reçue du serveur',
        });
      }
    } catch (error) {
      console.error('[ConfigStore] Failed to load config:', error);
      set({
        isLoading: false,
        error: 'Impossible de charger la configuration',
      });
    }
  },

  reset: () => {
    set(initialState);
  },
}));

export default useConfigStore;
